import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import { z } from "zod";
import { AccountUnknown } from "./routes/AccountUnknown";
import { BeschikbaarheidPage } from "./routes/BeschikbaarheidPage";
import { AccountPage } from "./routes/AccountPage";
import { InloglinkPage } from "./routes/Inloglink";
import { NotFound } from "./routes/NotFound";
import { OverzichtPage } from "./routes/OverzichtPage";
import { PlanningPage } from "./routes/PlanningPage";
import { PloegenPage } from "./routes/PloegenPage";
import { PloegPage } from "./routes/PloegPage";
import { SignInFailed } from "./routes/SignInFailed";
import { WedstrijddagPage } from "./routes/WedstrijddagPage";

/**
 * Code-based routes rather than file-based. The generated route tree would be a second
 * committed artifact with its own drift check in CI, and six routes do not earn one.
 */
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

/**
 * In the URL, so a reload or a shared link lands where it was; `.catch()`, because a stale korps
 * id is a page with nothing chosen, not a not-found. A korpscontact never sees the param.
 */
const ploegenSearch = z.object({
  korps: z.string().optional().catch(undefined),
  pagina: z.coerce.number().int().min(1).optional().catch(undefined),
  periode: z.string().optional().catch(undefined),
});

/**
 * `ploeg` absent is a new one, the same page either way, since the API takes the same body. Both
 * ids ride in the URL so a colleague can be sent the ploeg itself. `periode` names the window the
 * entry is made under - two stand open at once, so deriving it here would pick the wrong one.
 */
const ploegSearch = z.object({
  korps: z.string().optional().catch(undefined),
  ploeg: z.string().optional().catch(undefined),
  periode: z.string().optional().catch(undefined),
});

/**
 * Only for a session holding both halves; everybody else is sent on to theirs. No search params:
 * the korps a regiocontact picked belongs to the ploegen screen.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: OverzichtPage,
});

const ploegenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ploegen",
  component: PloegenPage,
  validateSearch: (search: Record<string, unknown>) => ploegenSearch.parse(search),
});

const beschikbaarheidRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/beschikbaarheid",
  component: BeschikbaarheidPage,
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planning",
  component: PlanningPage,
});

/**
 * One wedstrijddag's speelschema, opened from its block. A path segment rather than a search
 * param: it names *which* dag this page is, the way `/ploeg` is a screen that a korps and a
 * ploeg are chosen on.
 */
const wedstrijddagRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planning/$dagId",
  component: WedstrijddagPage,
});

const ploegRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ploeg",
  component: PloegPage,
  validateSearch: (search: Record<string, unknown>) => ploegSearch.parse(search),
});

const accountUnknownRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/unknown",
  component: AccountUnknown,
});

/** Where an inloglink lands. It consumes the link and then gets out of the way. */
const inloglinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/inloglink",
  component: InloglinkPage,
});

/** The only place a password is ever set, and what became of self-service reset. */
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

const signInFailedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/signin-failed",
  component: SignInFailed,
});

/** Exported so a test can build a router over the real routes with its own history. */
export const routeTree = rootRoute.addChildren([
  indexRoute,
  ploegenRoute,
  beschikbaarheidRoute,
  planningRoute,
  wedstrijddagRoute,
  ploegRoute,
  accountUnknownRoute,
  inloglinkRoute,
  accountRoute,
  signInFailedRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
